import { useEffect, useId, useRef } from 'react';
import { BarChart3, Radio } from 'lucide-react';
import DrawerHeader from '../studio/DrawerHeader';
import { FOCUSABLE_SELECTOR, focusableWithin, wrapTabFocus } from '../../lib/focusTrap';
import { formatTimestamp } from '../../lib/publisherFormat';
import { c } from '../../styles/theme';
import type {
  PublisherReleasePlatformCatalogEntry,
  PublisherReleasePlatformStats,
} from '../../types/domain';

export interface PublisherPlatformDrawerProps {
  entry: PublisherReleasePlatformCatalogEntry;
  stats: PublisherReleasePlatformStats | null;
  loading: boolean;
  onClose: () => void;
}

function MetricCard({
  label,
  value,
  note,
  muted,
}: {
  label: string;
  value: string;
  note?: string | null;
  muted?: boolean;
}) {
  return (
    <div
      style={{
        border: `1px solid ${c.border}`,
        borderRadius: 10,
        padding: '10px 12px',
        background: c.surface2,
      }}
    >
      <p
        style={{
          margin: '0 0 4px',
          fontSize: 10.5,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: c.text2,
          fontWeight: 600,
        }}
      >
        {label}
      </p>
      <p
        style={{
          margin: '0 0 4px',
          fontFamily: 'Space Grotesk',
          fontSize: 16,
          fontWeight: 600,
          color: muted ? c.text2 : c.text1,
        }}
      >
        {value}
      </p>
      {note ? <p style={{ margin: 0, fontSize: 11, color: c.text2, lineHeight: 1.5 }}>{note}</p> : null}
    </div>
  );
}

export default function PublisherPlatformDrawer({
  entry,
  stats,
  loading,
  onClose,
}: PublisherPlatformDrawerProps) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const headingId = useId();

  useEffect(() => {
    previouslyFocused.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const root = rootRef.current;
    if (root) {
      const firstFocusable =
        focusableWithin(root)[0] ?? root.querySelector<HTMLElement>(FOCUSABLE_SELECTOR) ?? root;
      firstFocusable.focus();
    }
    return () => {
      const target = previouslyFocused.current;
      previouslyFocused.current = null;
      if (target?.isConnected) target.focus();
    };
  }, []);

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const root = rootRef.current;
      if (!root) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      wrapTabFocus(root, event);
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [onClose]);

  const metrics = stats?.metrics ?? [];
  const rawProviderFields = stats?.rawProviderFields ?? [];

  return (
    <>
      <div className="lyx-drawer-overlay" onClick={onClose} aria-hidden="true" />
      <div
        ref={rootRef}
        className="lyx-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        tabIndex={-1}
        style={{
          borderLeft: `1px solid ${c.border}`,
          background: c.bg,
          padding: 16,
          boxShadow: '-14px 0 42px rgba(0,0,0,0.28)',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}
      >
        <div id={headingId} style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden' }}>
          {entry.outletName} platform details
        </div>
        <DrawerHeader title={entry.outletName} backLabel={entry.outletName} onBack={onClose} onClose={onClose} />

        <div
          style={{
            border: `1px solid ${c.border}`,
            borderRadius: 12,
            background: c.surface1,
            padding: '14px 16px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <div
              aria-hidden="true"
              style={{
                width: 32,
                height: 32,
                borderRadius: 9,
                background: c.surface2,
                border: `1px solid ${c.border}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <BarChart3 size={15} color={c.accent} />
            </div>
            <div style={{ minWidth: 0 }}>
              <p style={{ margin: '0 0 3px', fontSize: 13, fontWeight: 600, color: c.text1 }}>{entry.outletName}</p>
              <p style={{ margin: '0 0 4px', fontSize: 11.5, color: c.text2, lineHeight: 1.55 }}>
                {entry.hasReportedStatus
                  ? 'This drawer shows provider-backed delivery facts plus any platform metrics the backend could truthfully join to this outlet.'
                  : 'This outlet exists in the account catalogue, but no release-specific delivery row has been reported yet.'}
              </p>
              <p style={{ margin: 0, fontSize: 11, color: c.text2 }}>
                {stats?.reportedAt ? `Last reported ${formatTimestamp(stats.reportedAt)}` : 'No provider report timestamp yet'}
              </p>
            </div>
          </div>
        </div>

        {loading ? (
          <div
            style={{
              border: `1px solid ${c.border}`,
              borderRadius: 12,
              background: c.surface1,
              padding: '14px 16px',
            }}
          >
            <p style={{ margin: 0, fontSize: 12, color: c.text2 }}>Reading platform stats…</p>
          </div>
        ) : metrics.length === 0 ? (
          <div
            style={{
              border: `1px solid ${c.border}`,
              borderRadius: 12,
              background: c.surface1,
              padding: '14px 16px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 8,
            }}
          >
            <Radio size={15} color={c.text2} style={{ flexShrink: 0, marginTop: 1 }} />
            <div>
              <p style={{ margin: '0 0 4px', fontSize: 12.5, fontWeight: 500, color: c.text1 }}>No platform stats to report</p>
              <p style={{ margin: 0, fontSize: 11.5, color: c.text2, lineHeight: 1.55 }}>
                This outlet has no provider-backed platform metrics in the current response.
              </p>
            </div>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 8 }}>
            {metrics.map((item) => (
              <MetricCard
                key={item.key}
                label={item.label}
                value={item.value}
                note={item.note}
                muted={!item.reported}
              />
            ))}
          </div>
        )}

        {rawProviderFields.length > 0 ? (
          <section
            style={{
              border: `1px solid ${c.border}`,
              borderRadius: 12,
              background: c.surface1,
              padding: '14px 16px',
            }}
          >
            <p
              style={{
                margin: '0 0 10px',
                fontSize: 11,
                color: c.text2,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                fontWeight: 600,
              }}
            >
              Raw provider fields
            </p>
            <div style={{ display: 'grid', gap: 8 }}>
              {rawProviderFields.map((item) => (
                <MetricCard key={item.key} label={item.label} value={item.value} note={item.note} muted={!item.reported} />
              ))}
            </div>
          </section>
        ) : null}
      </div>
    </>
  );
}
